import React, { useRef, useEffect, useState, useMemo } from 'react';
import * as THREE from 'three';

interface RadiusNetworkVisualizationProps {
  className?: string;
}

const RadiusNetworkVisualization: React.FC<RadiusNetworkVisualizationProps> = ({ className = '' }) => {
  const mountRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const frameRef = useRef<number | null>(null);
  const mouseRef = useRef({ x: 0, y: 0 });
  const [isLoaded, setIsLoaded] = useState(false);

  // Create vendor nodes positions
  const vendorNodes = useMemo(() => {
    const nodes: Array<{ position: THREE.Vector3; id: number }> = [];
    const nodeCount = 60; // Number of surrounding nodes
    
    for (let i = 0; i < nodeCount; i++) {
      // Create random spherical distribution
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      const radius = 2.5 + Math.random() * 0.8; // Vary radius slightly for organic feel
      
      nodes.push({
        position: new THREE.Vector3(
          radius * Math.sin(phi) * Math.cos(theta),
          radius * Math.sin(phi) * Math.sin(theta),
          radius * Math.cos(phi)
        ),
        id: i
      });
    }
    
    return nodes;
  }, []);

  useEffect(() => {
    if (!mountRef.current) return;

    // Scene setup
    const scene = new THREE.Scene();
    scene.background = null; // Transparent background
    scene.fog = new THREE.Fog(0xffffff, 5, 15);
    sceneRef.current = scene;

    // Camera setup
    const camera = new THREE.PerspectiveCamera(
      60,
      mountRef.current.clientWidth / mountRef.current.clientHeight,
      0.1,
      100
    );
    camera.position.z = 7;

    // Renderer setup
    const renderer = new THREE.WebGLRenderer({ 
      antialias: true, 
      alpha: true // Enable transparency
    });
    renderer.setSize(mountRef.current.clientWidth, mountRef.current.clientHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2)); // Limit for performance
    renderer.setClearColor(0x000000, 0); // Transparent
    mountRef.current.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Lighting - Enhanced for better 3D effect
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.15);
    scene.add(ambientLight);

    // Multiple lights for better 3D definition on central node
    const pointLight = new THREE.PointLight(0x2563eb, 2, 10);
    pointLight.position.set(0, 0, 0);
    scene.add(pointLight);

    // Add directional lights for better 3D shading
    const directionalLight1 = new THREE.DirectionalLight(0xffffff, 0.5);
    directionalLight1.position.set(5, 5, 5);
    scene.add(directionalLight1);

    const directionalLight2 = new THREE.DirectionalLight(0x2563eb, 0.3);
    directionalLight2.position.set(-5, -5, 5);
    scene.add(directionalLight2);

    // Central node (client/buyer) - Enhanced materials for 3D effect
    const centerGeometry = new THREE.SphereGeometry(0.25, 64, 64);
    const centerMaterial = new THREE.MeshPhongMaterial({
      color: 0x4a7fff,
      emissive: 0x2563eb,
      emissiveIntensity: 0.3,
      shininess: 150,
      specular: 0xffffff,
      flatShading: false
    });
    const centerNode = new THREE.Mesh(centerGeometry, centerMaterial);
    scene.add(centerNode);

    // Add glow to center node - with improved material
    const glowGeometry = new THREE.SphereGeometry(0.35, 32, 32);
    const glowMaterial = new THREE.MeshPhongMaterial({
      color: 0x2563eb,
      transparent: true,
      opacity: 0.2,
      emissive: 0x2563eb,
      emissiveIntensity: 0.5,
      side: THREE.BackSide
    });
    const glowSphere = new THREE.Mesh(glowGeometry, glowMaterial);
    scene.add(glowSphere);

    // Create vendor nodes (surrounding points)
    const vendorMeshes: THREE.Mesh[] = [];
    const vendorGeometry = new THREE.SphereGeometry(0.06, 16, 16);
    
    vendorNodes.forEach(node => {
      const vendorMaterial = new THREE.MeshPhongMaterial({
        color: 0x64748b,
        emissive: 0x64748b,
        emissiveIntensity: 0.1
      });
      const vendorMesh = new THREE.Mesh(vendorGeometry, vendorMaterial);
      vendorMesh.position.copy(node.position);
      vendorMesh.userData = { 
        id: node.id, 
        originalColor: 0x64748b,
        illuminated: false,
        illuminationTime: 0
      };
      scene.add(vendorMesh);
      vendorMeshes.push(vendorMesh);
    });

    // Connection lines array
    const connections: Array<{ connection: Connection; targetMesh: THREE.Mesh }> = [];
    
    // Create connection line
    class Connection {
      startPos: THREE.Vector3;
      endPos: THREE.Vector3;
      progress: number;
      phase: 'traveling' | 'holding' | 'fading' | 'done';
      holdTime: number;
      fadeProgress: number;
      scene: THREE.Scene | THREE.Group;
      line: THREE.Line;
      particle: THREE.Mesh;

      constructor(startPos: THREE.Vector3, endPos: THREE.Vector3, scene: THREE.Scene | THREE.Group) {
        this.startPos = startPos;
        this.endPos = endPos;
        this.progress = 0;
        this.phase = 'traveling';
        this.holdTime = 0;
        this.fadeProgress = 0;
        this.scene = scene;
        
        // Create line
        const geometry = new THREE.BufferGeometry();
        const points = [startPos, startPos]; // Start with zero length
        geometry.setFromPoints(points);
        
        const material = new THREE.LineBasicMaterial({ 
          color: 0xf59e0b,
          transparent: true,
          opacity: 0.6,
          linewidth: 2
        });
        
        this.line = new THREE.Line(geometry, material);
        scene.add(this.line);
        
        // Create traveling particle
        const particleGeometry = new THREE.SphereGeometry(0.04, 8, 8);
        const particleMaterial = new THREE.MeshBasicMaterial({
          color: 0xfbbf24
        });
        this.particle = new THREE.Mesh(particleGeometry, particleMaterial);
        scene.add(this.particle);
      }
      
      update(deltaTime: number): { phase: string; completed: boolean } {
        if (this.phase === 'done') return { phase: 'done', completed: false };
        
        if (this.phase === 'traveling') {
          this.progress += deltaTime * 0.3; // Speed of connection
          
          if (this.progress >= 1) {
            this.progress = 1;
            this.phase = 'holding';
            // Remove particle when travel is complete
            this.scene.remove(this.particle);
            return { phase: 'holding', completed: true }; // Signal that connection just completed
          }
          
          // Update line length while traveling
          const currentEnd = new THREE.Vector3().lerpVectors(
            this.startPos,
            this.endPos,
            this.progress
          );
          
          const points = [this.startPos, currentEnd];
          this.line.geometry.setFromPoints(points);
          
          // Update particle position
          this.particle.position.lerpVectors(
            this.startPos,
            this.endPos,
            this.progress
          );
          
          // Maintain opacity while traveling
          (this.line.material as THREE.LineBasicMaterial).opacity = 0.6;
          
        } else if (this.phase === 'holding') {
          this.holdTime += deltaTime;
          
          // Hold the connection visible for a moment
          if (this.holdTime > 0.5) { // Hold for 0.5 seconds
            this.phase = 'fading';
          }
          
          // Full line remains visible
          const points = [this.startPos, this.endPos];
          this.line.geometry.setFromPoints(points);
          
        } else if (this.phase === 'fading') {
          this.fadeProgress += deltaTime * 0.5; // Fade speed
          
          if (this.fadeProgress >= 1) {
            this.phase = 'done';
            return { phase: 'done', completed: false };
          }
          
          // Gradually fade out the line
          (this.line.material as THREE.LineBasicMaterial).opacity = 0.6 * (1 - this.fadeProgress);
        }
        
        return { phase: this.phase, completed: false };
      }
      
      destroy() {
        this.scene.remove(this.line);
        if (this.particle.parent) {
          this.scene.remove(this.particle);
        }
      }
    }

    // Mouse movement handler
    const handleMouseMove = (event: MouseEvent) => {
      if (!mountRef.current) return;
      const rect = mountRef.current.getBoundingClientRect();
      mouseRef.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouseRef.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    };

    // Touch movement handler for mobile
    const handleTouchMove = (event: TouchEvent) => {
      if (event.touches.length > 0 && mountRef.current) {
        const rect = mountRef.current.getBoundingClientRect();
        const touch = event.touches[0];
        mouseRef.current.x = ((touch.clientX - rect.left) / rect.width) * 2 - 1;
        mouseRef.current.y = -((touch.clientY - rect.top) / rect.height) * 2 + 1;
      }
    };

    const currentMount = mountRef.current;
    currentMount.addEventListener('mousemove', handleMouseMove);
    currentMount.addEventListener('touchmove', handleTouchMove);

    // Container for all rotating elements
    const rotationGroup = new THREE.Group();
    scene.add(rotationGroup);
    
    // Move all nodes to rotation group
    rotationGroup.add(centerNode);
    rotationGroup.add(glowSphere);
    vendorMeshes.forEach(mesh => rotationGroup.add(mesh));

    // Animation variables
    let lastConnectionTime = 0;
    const connectionInterval = 800;
    const maxSimultaneousConnections = 5;
    let currentRotationY = 0;
    let currentRotationX = 0;
    let velocityY = 0.3; // Current velocity (unified - no dual-mode)
    let velocityX = 0;
    let hasMouseInteracted = false;
    let lastMouseX = 0;
    let lastMouseY = 0;
    const minRotationSpeed = 0.08;
    const velocityBlendFactor = 0.5; // How quickly new input blends into velocity
    const decayFactor = 0.995; // Consistent decay every frame

    // Animation loop
    const clock = new THREE.Clock();
    
    const animate = () => {
      frameRef.current = requestAnimationFrame(animate);
      
      const deltaTime = clock.getDelta();
      const elapsedTime = clock.getElapsedTime();
      
      // Check for mouse interaction
      const mouseActive = Math.abs(mouseRef.current.x) > 0.01 || Math.abs(mouseRef.current.y) > 0.01;
      
      if (!hasMouseInteracted && mouseActive) {
        hasMouseInteracted = true;
        lastMouseX = mouseRef.current.x;
        lastMouseY = mouseRef.current.y;
        velocityY = 0;
      }
      
      if (!hasMouseInteracted) {
        // Default rotation before mouse interaction
        currentRotationY += velocityY * deltaTime;
        currentRotationX = 0;
      } else {
        // Mouse-influenced rotation with immediate response and smooth momentum
        const mouseDeltaX = mouseRef.current.x - lastMouseX;
        const mouseDeltaY = mouseRef.current.y - lastMouseY;
        
        const mouseIsMoving = Math.abs(mouseDeltaX) > 0.0005 || Math.abs(mouseDeltaY) > 0.0005;
        
        if (mouseIsMoving) {
          // Calculate target velocity from mouse movement
          const targetVelocityY = mouseDeltaX * 10;
          const targetVelocityX = -mouseDeltaY * 10;
          
          // Blend new velocity into current velocity (immediate but smooth)
          velocityY = velocityY * (1 - velocityBlendFactor) + targetVelocityY * velocityBlendFactor;
          velocityX = velocityX * (1 - velocityBlendFactor) + targetVelocityX * velocityBlendFactor;
        }
        
        // Always apply decay (whether mouse is moving or not)
        velocityY *= decayFactor;
        velocityX *= decayFactor;
        
        // Ensure minimum baseline rotation speed for Y axis
        if (Math.abs(velocityY) < minRotationSpeed) {
          velocityY = velocityY >= 0 ? minRotationSpeed : -minRotationSpeed;
        }
        
        // Let X axis decay to zero naturally
        if (Math.abs(velocityX) < 0.001) {
          velocityX = 0;
        }
        
        // Apply velocity to rotation
        currentRotationY += velocityY * deltaTime;
        currentRotationX += velocityX * deltaTime;
        
        // Clamp X rotation
        currentRotationX = Math.max(-Math.PI * 0.5, Math.min(Math.PI * 0.5, currentRotationX));
        
        lastMouseX = mouseRef.current.x;
        lastMouseY = mouseRef.current.y;
      }
      
      rotationGroup.rotation.y = currentRotationY;
      rotationGroup.rotation.x = currentRotationX;
      
      // Center node pulsing
      const pulseFactor = 1 + Math.sin(elapsedTime * 2) * 0.08;
      centerNode.scale.setScalar(pulseFactor);
      glowSphere.scale.setScalar(pulseFactor * 1.4);
      glowSphere.material.opacity = 0.2 + Math.sin(elapsedTime * 3) * 0.05;
      
      // Create new connections periodically
      const currentTime = Date.now();
      if (currentTime - lastConnectionTime > connectionInterval && connections.length < maxSimultaneousConnections) {
        const randomVendor = vendorMeshes[Math.floor(Math.random() * vendorMeshes.length)];
        if (!randomVendor.userData.illuminated) {
          const connection = new Connection(
            new THREE.Vector3(0, 0, 0),
            randomVendor.position.clone(),
            rotationGroup
          );
          connections.push({ 
            connection, 
            targetMesh: randomVendor 
          });
          lastConnectionTime = currentTime;
        }
      }
      
      // Update connections
      connections.forEach((conn) => {
        const result = conn.connection.update(deltaTime);
        
        if (result.completed) {
          conn.targetMesh.userData.illuminated = true;
          conn.targetMesh.userData.illuminationTime = elapsedTime;
          (conn.targetMesh.material as THREE.MeshPhongMaterial).color.setHex(0xfbbf24);
          (conn.targetMesh.material as THREE.MeshPhongMaterial).emissive.setHex(0xfbbf24);
          (conn.targetMesh.material as THREE.MeshPhongMaterial).emissiveIntensity = 0.5;
        }
      });
      
      // Remove connections that are completely done
      for (let i = connections.length - 1; i >= 0; i--) {
        if (connections[i].connection.phase === 'done') {
          connections[i].connection.destroy();
          connections.splice(i, 1);
        }
      }
      
      // Fade out illuminated vendor nodes
      vendorMeshes.forEach(mesh => {
        if (mesh.userData.illuminated) {
          const timeSinceIllumination = elapsedTime - mesh.userData.illuminationTime;
          
          if (timeSinceIllumination > 2) {
            const fadeFactor = Math.max(0, 1 - (timeSinceIllumination - 2) / 2);
            
            if (fadeFactor <= 0) {
              mesh.userData.illuminated = false;
              (mesh.material as THREE.MeshPhongMaterial).color.setHex(mesh.userData.originalColor);
              (mesh.material as THREE.MeshPhongMaterial).emissive.setHex(mesh.userData.originalColor);
              (mesh.material as THREE.MeshPhongMaterial).emissiveIntensity = 0.1;
            } else {
              const r = Math.floor(0x64 + (0xfb - 0x64) * fadeFactor);
              const g = Math.floor(0x74 + (0xbf - 0x74) * fadeFactor);
              const b = Math.floor(0x8b + (0x24 - 0x8b) * fadeFactor);
              const color = (r << 16) | (g << 8) | b;
              
              (mesh.material as THREE.MeshPhongMaterial).color.setHex(color);
              (mesh.material as THREE.MeshPhongMaterial).emissive.setHex(color);
              (mesh.material as THREE.MeshPhongMaterial).emissiveIntensity = 0.1 + 0.4 * fadeFactor;
            }
          }
        }
      });
      
      renderer.render(scene, camera);
    };
    
    animate();
    setIsLoaded(true);

    // Handle resize
    const handleResize = () => {
      if (!mountRef.current) return;
      
      camera.aspect = mountRef.current.clientWidth / mountRef.current.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(mountRef.current.clientWidth, mountRef.current.clientHeight);
    };
    
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }
      
      window.removeEventListener('resize', handleResize);
      
      if (currentMount) {
        currentMount.removeEventListener('mousemove', handleMouseMove);
        currentMount.removeEventListener('touchmove', handleTouchMove);
        
        if (currentMount.contains(renderer.domElement)) {
          currentMount.removeChild(renderer.domElement);
        }
      }
      
      // Dispose of Three.js resources
      scene.traverse(object => {
        if ((object as THREE.Mesh).geometry) (object as THREE.Mesh).geometry.dispose();
        if ((object as THREE.Mesh).material) {
          const material = (object as THREE.Mesh).material;
          if (Array.isArray(material)) {
            material.forEach(mat => mat.dispose());
          } else {
            material.dispose();
          }
        }
      });
      
      renderer.dispose();
    };
  }, [vendorNodes]);

  return (
    <div 
      className={className}
      style={{ 
        width: '100%', 
        height: '100%',
        position: 'absolute',
        top: 0,
        left: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
      }}
    >
      <div 
        ref={mountRef} 
        style={{ 
          width: '100%', 
          height: '100%',
          cursor: 'grab',
          pointerEvents: 'auto',
        }}
      />
      
      {!isLoaded && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          color: 'hsl(var(--muted-foreground))',
          fontSize: '1rem',
        }}>
          Loading visualization...
        </div>
      )}
    </div>
  );
};

export default RadiusNetworkVisualization;
